import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Activity, Globe, Lock, ClipboardCheck, ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/i18n";
import { scrollToId } from "@/lib/scroll";
import SectionHead, { EASE, reveal } from "@/components/SectionHead";

const UPTIME_BARS = [96, 92, 98, 88, 95, 99, 93, 97, 90, 98, 94, 99, 96, 100];

function LiveClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <span className="font-mono text-sm font-semibold text-amber-300 [text-shadow:0_0_16px_rgba(52,211,153,0.5)]" data-testid="live-clock">
      {now.toUTCString().slice(17, 25)} UTC
    </span>
  );
}

export default function Why() {
  const { t } = useLanguage();

  const cardCls =
    "group relative overflow-hidden rounded-2xl border border-slate-800/60 bg-slate-900/40 p-8 backdrop-blur-sm transition-all duration-500 hover:-translate-y-1 hover:border-orange-400/40 hover:shadow-[0_16px_50px_rgba(234,88,12,0.14)]";

  return (
    <section id="why" className="relative py-28 sm:py-32" data-testid="why-section">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-400/40 to-transparent" />
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead eyebrow={t.why.eyebrow} title={t.why.h2} testid="why-title" />

        <div className="mt-16 grid gap-6 md:grid-cols-12">
          <motion.div {...reveal} transition={{ duration: 0.8, ease: EASE }} data-testid="why-card-24-7" className={`${cardCls} md:col-span-7 md:row-span-2 flex flex-col`}>
            <div className="flex items-center justify-between">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-700/50 bg-slate-950/60 text-orange-300">
                <Activity className="h-5 w-5" />
              </span>
              <span className="flex items-center gap-2.5 rounded-full border border-amber-400/30 bg-amber-400/5 px-3.5 py-1.5">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-60" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-400" />
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-300">
                  {t.why.live}
                </span>
              </span>
            </div>

            <h3 className="mt-8 font-display text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">
              {t.why.c1title}
            </h3>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-400 sm:text-base">
              {t.why.c1body}
            </p>

            <div className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-slate-700/40 bg-slate-950/60 px-5 py-4">
              <LiveClock />
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                NOC · 24/7/365
              </span>
            </div>

            <div className="mt-6 flex h-20 items-end gap-2">
              {UPTIME_BARS.map((b, i) => (
                <motion.div
                  key={i}
                  className="flex-1 rounded-sm bg-gradient-to-t from-slate-700 to-amber-400/80"
                  initial={{ height: "6%" }}
                  whileInView={{ height: `${b}%` }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.7, delay: i * 0.05, ease: EASE }}
                />
              ))}
            </div>
          </motion.div>

          <motion.div {...reveal} transition={{ duration: 0.75, delay: 0.1, ease: EASE }} data-testid="why-card-crossborder" className={`${cardCls} md:col-span-5`}>
            <div className="flex items-center justify-between">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-700/50 bg-slate-950/60 text-orange-300">
                <Globe className="h-5 w-5" />
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">EU · UAE</span>
            </div>
            <h3 className="mt-8 font-display text-2xl font-bold tracking-tight text-slate-50">{t.why.c2title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-400 sm:text-base">{t.why.c2body}</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {["Europe", "Middle East", "Worldwide"].map((r) => (
                <span key={r} className="rounded-full border border-slate-700/50 bg-slate-950/50 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-slate-400">
                  {r}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div {...reveal} transition={{ duration: 0.75, delay: 0.2, ease: EASE }} data-testid="why-card-security" className={`${cardCls} md:col-span-5`}>
            <div className="flex items-center justify-between">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-700/50 bg-slate-950/60 text-amber-300">
                <Lock className="h-5 w-5" />
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">GDPR</span>
            </div>
            <h3 className="mt-8 font-display text-2xl font-bold tracking-tight text-slate-50">{t.why.c3title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-400 sm:text-base">{t.why.c3body}</p>
          </motion.div>

          <motion.div {...reveal} transition={{ duration: 0.8, delay: 0.15, ease: EASE }} data-testid="why-card-hasslefree" className={`${cardCls} md:col-span-12`}>
            <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
              <div className="max-w-xl">
                <div className="flex items-center gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-700/50 bg-slate-950/60 text-orange-300">
                    <ClipboardCheck className="h-5 w-5" />
                  </span>
                  <h3 className="font-display text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">
                    {t.why.c4title}
                  </h3>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-400 sm:text-base">{t.why.c4body}</p>
              </div>

              <div className="flex flex-wrap items-center gap-3 lg:flex-col lg:items-stretch">
                {t.why.pipeline.map((step, i) => (
                  <div key={step} className="flex items-center gap-3">
                    <span className="rounded-full border border-slate-700/50 bg-slate-950/50 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-orange-300">
                      {step}
                    </span>
                    {i < t.why.pipeline.length - 1 && (
                      <span className="h-px w-6 bg-gradient-to-r from-orange-400/60 to-amber-400/60 lg:hidden" />
                    )}
                  </div>
                ))}
                <button
                  data-testid="why-cta"
                  onClick={() => scrollToId("#contact")}
                  className="group mt-2 flex items-center justify-center gap-2 rounded-full border border-orange-400/40 px-5 py-2.5 text-sm font-semibold text-orange-300 transition-colors duration-300 hover:bg-orange-400/10"
                >
                  {t.nav.cta}
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
